import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { AbstractControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { SlFormControl } from '../../../core/abstractions/sl-form-control';
import { CustomValidators } from '../../../core/utils/custom-validators';
import { AgendaItemSettingsModel } from '../../../models/agenda-item-settings.model';
import { VoteOptionEnum } from '../../../models/enums/vote-option.enum';
import { OptionalType } from '../../../models/types/optional.type';
import { GeneralMeetingsResource } from '../../../services/resources/general-meetings.resource';
import { TranslationService } from '../../../services/translation.service';

@Component({
    selector: 'sl-agenda-item-settings',
    templateUrl: './agenda-item-settings.component.html',
    styleUrls: ['./agenda-item-settings.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AgendaItemSettingsComponent implements OnInit {
    readonly VoteOptionEnum = VoteOptionEnum;

    readonly isLoading = signal(true);
    readonly dialogTitle = signal('');

    readonly formGroup = new FormGroup(
        {
            number: new SlFormControl('', { nonNullable: true, validators: Validators.required }),
            votingCouponNumber: new SlFormControl<OptionalType<number>>(undefined, CustomValidators.number({ isInteger: true })),
            showOnNetvote: new SlFormControl<boolean>(false),
            excludeManagementOrBoardOfDirectors: new SlFormControl<boolean>(false),
            capitalOnly: new SlFormControl<boolean>(false),
            submittedVotesOnly: new SlFormControl<boolean>(false),
            onlyYesNoVotes: new SlFormControl<boolean>(false),
            qualifiedVote: new SlFormControl<OptionalType<boolean>>(undefined),
            descriptionGerman: new SlFormControl<OptionalType<string>>(undefined, Validators.maxLength(400)),
            descriptionFrench: new SlFormControl<OptionalType<string>>(undefined, Validators.maxLength(400)),
            descriptionEnglish: new SlFormControl<OptionalType<string>>(undefined, Validators.maxLength(400)),
            descriptionItalian: new SlFormControl<OptionalType<string>>(undefined, Validators.maxLength(400)),
            descriptionVotingGerman: new SlFormControl<OptionalType<string>>(undefined, Validators.maxLength(400)),
            descriptionVotingFrench: new SlFormControl<OptionalType<string>>(undefined, Validators.maxLength(400)),
            descriptionVotingEnglish: new SlFormControl<OptionalType<string>>(undefined, Validators.maxLength(400)),
            descriptionVotingItalian: new SlFormControl<OptionalType<string>>(undefined, Validators.maxLength(400))
        },
        {
            validators: this.atLeastOneRequiredValidator([
                'descriptionGerman',
                'descriptionItalian',
                'descriptionFrench',
                'descriptionEnglish'
            ])
        }
    );

    agendaItemSettings = signal<OptionalType<AgendaItemSettingsModel>>(undefined);

    private readonly data = inject<{ agendaItemNumber: number }>(MAT_DIALOG_DATA);
    private readonly dialogRef = inject(MatDialogRef);
    private readonly generalMeetingsResource = inject(GeneralMeetingsResource);
    private readonly translationService = inject(TranslationService);

    ngOnInit(): void {
        this.generalMeetingsResource.getCurrentAgendaItemSettings(this.data.agendaItemNumber).subscribe(r => {
            this.formGroup.patchValue(r.value);

            this.agendaItemSettings.set(r.value);

            let currentDescription = r.value.descriptionGerman;
            switch (this.translationService.getCurrent().code) {
                case 'en':
                    currentDescription = r.value.descriptionEnglish || currentDescription;
                    break;
                case 'it':
                    currentDescription = r.value.descriptionItalian || currentDescription;
                    break;
                case 'fr':
                    currentDescription = r.value.descriptionFrench || currentDescription;
                    break;
            }

            this.dialogTitle.set(`${r.value.number} ${currentDescription}`);
            this.isLoading.set(false);
        });
    }

    onSave(): void {
        this.formGroup.markAllAsTouched();
        this.formGroup.updateValueAndValidity();
        const agendaItemSettings = this.agendaItemSettings();
        if (this.formGroup.invalid || !agendaItemSettings) {
            return;
        }

        const value = this.formGroup.getRawValue();
        this.generalMeetingsResource
            .updateCurrentAgendaItemSettings({
                ...agendaItemSettings,
                number: value.number,
                votingCouponNumber: value.votingCouponNumber || undefined,
                showOnNetvote: value.showOnNetvote || false,
                excludeManagementOrBoardOfDirectors: value.excludeManagementOrBoardOfDirectors || false,
                capitalOnly: value.capitalOnly || false,
                submittedVotesOnly: value.submittedVotesOnly || false,
                onlyYesNoVotes: value.onlyYesNoVotes || false,
                qualifiedVote: value.qualifiedVote || undefined,
                descriptionGerman: value.descriptionGerman || undefined,
                descriptionFrench: value.descriptionFrench || undefined,
                descriptionEnglish: value.descriptionEnglish || undefined,
                descriptionItalian: value.descriptionItalian || undefined,
                descriptionVotingGerman: value.descriptionVotingGerman || undefined,
                descriptionVotingFrench: value.descriptionVotingFrench || undefined,
                descriptionVotingEnglish: value.descriptionVotingEnglish || undefined,
                descriptionVotingItalian: value.descriptionVotingItalian || undefined
            })
            .subscribe(result => {
                if (result.value.isSuccessful) {
                    this.dialogRef.close();
                }
            });
    }

    descriptionErrorStateMatcher(): ErrorStateMatcher {
        const formGroup = this.formGroup;
        return {
            isErrorState(): boolean {
                return formGroup.hasError('atLeastOneRequired');
            }
        };
    }

    private atLeastOneRequiredValidator(fields: string[]): ValidatorFn {
        return (group: AbstractControl): ValidationErrors | null => {
            if (!(group instanceof FormGroup)) {
                // eslint-disable-next-line no-null/no-null
                return null;
            }

            // search any description control with value
            const hasAtLeastOneFilled = fields.some(f => !!group.controls[f].value);
            // eslint-disable-next-line no-null/no-null
            return hasAtLeastOneFilled ? null : { atLeastOneRequired: true };
        };
    }
}
